import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { AutoComplete } from 'primereact/autocomplete';
import Layout from '../component/layout';
import { makeRequest2 } from '../../../utils/makeRequest';
import { getFacilitiesURL, waterMeterBaseUrl } from '../../../utils/urls';
import { toastify } from '../../../utils/toast';
import MeterDeliveryNote from './delivery_note';
import { ConfirmDialog } from 'primereact/confirmdialog';

const AssignMeter = () => {
  const [currentStep, setCurrentStep] = useState('facility');
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [meters, setMeters] = useState([]);
  const [filteredMeters, setFilteredMeters] = useState([]);
  const [selectedMeters, setSelectedMeters] = useState([]);
  const [concentrators, setConcentrators] = useState([]);
  const [filteredConcentrators, setFilteredConcentrators] = useState([]);
  const [selectedConcentrators, setSelectedConcentrators] = useState([]);
  const [concentratorSearchTerm, setConcentratorSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const concentratorBaseUrl = '/api/core/concentrators';
  const [deliveryForm, setDeliveryForm] = useState({
    deliveryDate: '',
    deliveredBy: '',
    notes: ''
  });
  // -------------------- FETCH FUNCTIONS --------------------

  const getFacilities = async () => {
    try {
      const response = await makeRequest2(getFacilitiesURL, 'GET', {});
      if (response.success) {
        setFacilities(response.data);
      }
    } catch (error) {
      toastify(error.message, 'error');
    }
  };

  const fetchMeters = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(waterMeterBaseUrl, 'GET');
      if (response.success) {
        const metersArray = Array.isArray(response.data) ? response.data : response.data.meters;
        const uninstalledMeters = metersArray.filter(meter => {
          return !meter.isInstalled || meter.isInstalled === "false";
        });
        setMeters(uninstalledMeters);
        setFilteredMeters(uninstalledMeters);
      } else {
        toastify(response.error || 'Failed to fetch meters', 'error');
      }
    } catch (error) {
      toastify('Error fetching meters', 'error');
      console.error('Error fetching meters:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConcentrators = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(concentratorBaseUrl, 'GET');
      if (response.success) {
        const concentratorsArray = Array.isArray(response.data)
          ? response.data
          : response.data.concentrators;
        setConcentrators(concentratorsArray || []);
      } else {
        toastify(response.error || 'Failed to fetch concentrators', 'error');
      }
    } catch (error) {
      console.error('Error fetching concentrators:', error);
      toastify('An error occurred while fetching concentrators', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getFacilities();
    fetchMeters();
    fetchConcentrators();
  }, []);

  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = meters.filter(meter =>
        meter.serialNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (meter.manufacturer &&
          meter.manufacturer.name?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredMeters(filtered);
    } else {
      setFilteredMeters(meters);
    }
  }, [searchTerm, meters]);

  const searchConcentrator = (event) => {
    const query = event.query ? event.query.toLowerCase() : '';
    if (!query) {
      setFilteredConcentrators(concentrators);
    } else {
      const filtered = concentrators.filter((concentrator) => {
        const serial = concentrator.serialNumber?.toLowerCase() || '';
        let manufacturerName = '';
        if (concentrator.manufacturer) {
          if (typeof concentrator.manufacturer === 'object') {
            manufacturerName = concentrator.manufacturer.name?.toLowerCase() || '';
          } else {
            manufacturerName = concentrator.manufacturer.toLowerCase();
          }
        }
        return serial.includes(query) || manufacturerName.includes(query);
      });
      setFilteredConcentrators(filtered);
    }
  };

  const manufacturerTemplate = (rowData) =>
    rowData.manufacturer ? rowData.manufacturer.name : 'N/A';
  const sizeTemplate = (rowData) =>
    rowData.size ? rowData.size.code : 'N/A';

  // -------------------- CONCENTRATOR SELECTION --------------------

  const handleAddConcentrator = (concentrator) => {
    if (!selectedConcentrators.some(c => c._id === concentrator._id)) {
      setSelectedConcentrators(prev => [...prev, concentrator]);
      setConcentratorSearchTerm(''); 
    }
  };

  const handleRemoveConcentrator = (indexToRemove) => {
    setSelectedConcentrators(prev =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  // -------------------- DELIVERY FORM --------------------

  const handleDeliveryFormChange = (e) => {
    const { name, value } = e.target;
    setDeliveryForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFinishAssignment = async () => {
    try {
      // Find selected facility details
      const facility = facilities.find(f => f._id === selectedFacility);
      const metersData = selectedMeters.map(meter => ({
        meterId: meter._id,
        meterNumber: meter.meterNumber
      }));

      const concentratorsData = selectedConcentrators.map(concentrator => ({
        concentratorId: concentrator._id,
        serialNumber: concentrator.serialNumber
      }));

      const deliveryData = {
        facilityId: selectedFacility,
        facilityName: facility.name,
        deliveredBy: deliveryForm.deliveredBy,
        deliveryDate: new Date(deliveryForm.deliveryDate).toISOString(),
        notes: deliveryForm.notes || '',
        concentrators: concentratorsData,
        meters: metersData,
        status: 'pending'
      };

      // 1. Create delivery record
      const deliveryRes = await makeRequest2(
        '/api/core/water-meters-delivery',
        'POST',
        deliveryData
      );

      if (!deliveryRes.success) {
        throw new Error('Failed to create delivery record');
      }

      // 2. Update meters with facility ID
      const metersUpdatePromises = selectedMeters.map(meter =>
        makeRequest2(`${waterMeterBaseUrl}/${meter._id}`, 'PUT', {
          facilityId: selectedFacility,
        })
      );
      await Promise.all(metersUpdatePromises);

      // 3. Update concentrators with facility ID and mark as not in stock
      const concentratorsUpdatePromises = selectedConcentrators.map(concentrator =>
        makeRequest2(`${concentratorBaseUrl}/${concentrator._id}`, 'PUT', {
          facilityId: selectedFacility,
          inStock: false
        })
      );
      await Promise.all(concentratorsUpdatePromises);

      toastify('Assignment completed successfully', 'success');
      setCurrentStep('finish');
    } catch (error) {
      toastify(error.message || 'Failed to complete assignment', 'error');
    }
  };

  const handleNextClick = () => {
    const steps = ['facility', 'meter', 'concentrator', 'delivery', 'finish'];
    const currentIndex = steps.indexOf(currentStep);

    if (currentStep === 'delivery') {
      // Validate delivery form
      if (!deliveryForm.deliveryDate || !deliveryForm.deliveredBy) {
        toastify('Please fill in all required fields', 'error');
        return;
      }
      handleFinishAssignment();
    } else {
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  // -------------------- STEP RENDERING --------------------

  const renderFacilityStep = () => (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-body">
            <div className="form-group">
              <label>
                Select Facility<span style={{ color: 'red' }}>*</span>
              </label>
              <select
                className="form-control mt-2"
                value={selectedFacility}
                onChange={(e) => setSelectedFacility(e.target.value)}
              >
                <option value="">-- Select Facility --</option>
                {facilities.map((facility) => (
                  <option key={facility._id} value={facility._id}>
                    {facility.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMeterStep = () => (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-6">
                <InputText
                  placeholder="Search by meter number or manufacturer"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control"
                />
              </div>
              <div className="col-md-6 text-end">
                <span className="text-muted">
                  Selected Meters: {selectedMeters.length}
                </span>
              </div>
            </div>
            <div className="dt-responsive table-responsive">
              <DataTable
                value={filteredMeters}
                selection={selectedMeters}
                onSelectionChange={(e) => setSelectedMeters(e.value)}
                loading={loading}
                paginator
                rows={10}
                stripedRows
                emptyMessage="No uninstalled meters available."
                tableStyle={{ minWidth: '50rem' }}
                dataKey="_id"
              >
                <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>
                <Column field="serialNumber" sortable header="Serial Number"></Column>
                <Column
                  field="manufacturer"
                  sortable
                  header="Manufacturer"
                  body={(rowData) => manufacturerTemplate(rowData)}
                ></Column>
                <Column
                  field="size"
                  sortable
                  header="Size"
                  body={(rowData) => sizeTemplate(rowData)}
                ></Column>
                <Column field="initialReading" sortable header="Initial Reading (m³)"></Column>
              </DataTable>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderConcentratorStep = () => (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-body">
            <div className="form-group">
              <label>Select Concentrators</label>
              <div className="d-flex align-items-center mb-2">
                <AutoComplete
                  value={concentratorSearchTerm}
                  suggestions={filteredConcentrators}
                  completeMethod={searchConcentrator}
                  field="serialNumber"
                  dropdown
                  className="flex-grow-1 me-2"
                  onChange={(e) => setConcentratorSearchTerm(e.value)}
                  onSelect={(e) => handleAddConcentrator(e.value)}
                  itemTemplate={(item) => (
                    <div>
                      <div>
                        <strong>S/N:</strong> {item.serialNumber}
                      </div>
                      <div>
                        <small>
                          Manufacturer:{' '}
                          {item.manufacturer && typeof item.manufacturer === 'object'
                            ? item.manufacturer.name
                            : item.manufacturer || 'N/A'}
                        </small>
                      </div>
                    </div>
                  )}
                  placeholder="Search for a concentrator..."
                />
              </div>
              {selectedConcentrators.length > 0 && (
                <div className="selected-concentrators mt-2 d-flex flex-wrap gap-2">
                  {selectedConcentrators.map((concentrator, index) => (
                    <div
                      key={concentrator._id}
                      className="badge bg-primary d-flex align-items-center p-2 me-2 mb-2"
                    >
                      <span>{concentrator.serialNumber}</span>
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-white p-0 ms-2"
                        onClick={() => handleRemoveConcentrator(index)}
                        style={{ textDecoration: 'none' }}
                      >
                        <i className="ti ti-x"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDeliveryReport = () => (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-body">
            <h5>Delivery Report</h5>
            <hr />
            <div className="form-group">
              <label>
                Delivery Date<span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="date"
                name="deliveryDate"
                className="form-control mt-2"
                value={deliveryForm.deliveryDate}
                onChange={handleDeliveryFormChange}
                required
              />
            </div>
            <div className="form-group">
              <label>
                Enter Your Name<span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                name="deliveredBy"
                className="form-control mt-2"
                value={deliveryForm.deliveredBy}
                onChange={handleDeliveryFormChange}
                required
              />
            </div>
            <div className="form-group mt-3">
              <label>Comments</label>
              <textarea
                name="notes"
                className="form-control mt-2"
                rows="4"
                value={deliveryForm.notes}
                onChange={handleDeliveryFormChange}
                placeholder="Enter any additional comments"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFinishStep = () => (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-body text-center">
            <i
              className="ti ti-circle-check"
              style={{ fontSize: '48px', color: '#28a745' }}
            ></i>
            <h3 className="mt-3">Assignment Complete</h3>
            <p>The meter has been successfully assigned to the facility</p>
            <MeterDeliveryNote
              facility={facilities.find((f) => f._id === selectedFacility)}
              selectedMeters={selectedMeters}
              selectedConcentrators={selectedConcentrators}
              deliveryForm={deliveryForm}
            />
            <Link to="/core/water_meter_management/meters" className="btn btn-primary mt-3">
              Back to Meters
            </Link>
          </div>
        </div>
      </div>
    </div>
  );

  // -------------------- RENDERING --------------------
  return (
    <Layout>
      <ConfirmDialog />
      <div className="page-header">
        <ul className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/core/dashboard">Dashboard</Link>
          </li>
          <li className="breadcrumb-item">Water Meter Management</li>
          <li className="breadcrumb-item active">Assign</li>
        </ul>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <div className="card-body py-0">
                <ul className="nav nav-tabs profile-tabs" id="myTab" role="tablist">
                  <li className="nav-item">
                    <span
                      className={`nav-link ${currentStep === 'facility' ? 'active' : ''}`}
                      role="tab"
                    >
                      <i className="ti ti-building me-2"></i>
                      <span>Select Facility</span>
                    </span>
                  </li>
                  <li className="nav-item">
                    <span
                      className={`nav-link ${currentStep === 'meter' ? 'active' : ''}`}
                      role="tab"
                    >
                      <i className="ti ti-gauge me-2"></i>
                      <span>Select Meter</span>
                    </span>
                  </li>
                  <li className="nav-item">
                    <span
                      className={`nav-link ${currentStep === 'concentrator' ? 'active' : ''}`}
                      role="tab"
                    >
                      <i className="ti ti-settings me-2"></i>
                      <span>Concentrator</span>
                    </span>
                  </li>
                  <li className="nav-item">
                    <span
                      className={`nav-link ${currentStep === 'delivery' ? 'active' : ''}`}
                      role="tab"
                    >
                      <i className="ti ti-clipboard-check me-2"></i>
                      <span>Delivery Report</span>
                    </span>
                  </li>
                  <li className="nav-item">
                    <span
                      className={`nav-link ${currentStep === 'finish' ? 'active' : ''}`}
                      role="tab"
                    >
                      <i className="ti ti-check-circle me-2"></i>
                      <span>Finish</span>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="card-body">
              <div className="tab-content">
                <div className="tab-pane show active">
                  {currentStep === 'facility' && renderFacilityStep()}
                  {currentStep === 'meter' && renderMeterStep()}
                  {currentStep === 'concentrator' && renderConcentratorStep()}
                  {currentStep === 'delivery' && renderDeliveryReport()}
                  {currentStep === 'finish' && renderFinishStep()}
                  {currentStep !== 'finish' && (
                    <div className="row mt-3">
                      <div className="col-12">
                        <div style={{ float: 'right' }}>
                          {currentStep !== 'facility' && (
                            <button
                              className="btn btn-secondary me-2"
                              onClick={() => {
                                const steps = ['facility', 'meter', 'concentrator', 'delivery', 'finish'];
                                const currentIndex = steps.indexOf(currentStep);
                                setCurrentStep(steps[currentIndex - 1]);
                              }}
                            >
                              Previous
                            </button>
                          )}
                          <button
                            className="btn btn-primary"
                            onClick={handleNextClick}
                            disabled={
                              (currentStep === 'facility' && !selectedFacility) ||
                              (currentStep === 'meter' && selectedMeters.length === 0) ||
                              (currentStep === 'concentrator' && selectedConcentrators.length === 0) ||
                              (currentStep === 'delivery' &&
                                (!deliveryForm.deliveryDate || !deliveryForm.deliveredBy))
                            }
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AssignMeter;
